// Top-navbar tab context menu — right-clicking a HeaderTabs tab opens the
// shared house ContextDropdown (`root.contextDropdown`) with Pin / Open in
// side view / Remove, per Nika (2026-07-27 "add context menu to navbar items,
// where you can remove, pin and open in sideview"). Mirror of
// accountMenuItems / accountMenuAction — same registered itemsFn/actionFn
// contract, same MenuPanel styling for free.
//
// Persistence: per-user × per-workspace, on the SAME flat userPrefs row the
// boot already awaits pre-reveal (loadPrivateData `_prefsChain` →
// root.userPrefs), under `navbar_tabs: { [workspaceId]: { hidden: [paths],
// pinned: [paths] } }`. The tab's `path` is the identity — every navbar entry
// carries one (HEADER_TABS and workspaceSettings.navbar rows alike), unlike
// `key` which saved module navbars may omit. Writes mirror MemberProfile's
// prefs save: optimistic root.update, then the full merged bag through
// `workspaceProject.userPreferences · update` (server upserts the row).
//
// `pinnedCustom` (2026-08-02 per Nika "add in searchdropdown items pin to
// navbar in context menu") — CUSTOM pins: a foreign `{ path, label }` (a
// global-search result, workspace/functions/navSearchResultMenu.js) that
// isn't part of HEADER_TABS / workspaceSettings.navbar at all. Unlike
// `pinned` (a reorder-to-front hint for a row that already exists in the
// base list), a custom pin IS the row's only reason to exist in the
// navbar — applyNavbarTabPrefs synthesizes it its own `{ key, label, path }`
// entry (see below); unpinning removes the row outright, not just its rank.
import { getSDK } from './sdk.js'

// FA204/FA207 / frank trap — an exported function in `functions/*.js` must
// never read a module-scope const, nor bare-reference a SIBLING export:
// both are equally unresolvable once this file's exports are individually
// frank.toJSON'd for platform delivery (project_frank_drops_module_local_
// helpers; the readerVoice.js precedent, workspace 8732aa07 — `readerStop`/
// `readerBoundaryFor` were both EXPORTED and still broke bare). Fix shapes
// used throughout this file:
//   - `navbarTabPrefs`/`applyNavbarTabPrefs`/`toggleCustomNavbarPin` are
//     plain arrows with no `this` (consumed via plain ESM import from
//     component files — HeaderTabs.js, AppNavbar.js — never via el.call),
//     so cross-references are INLINED as self-contained locals rather than
//     `this.call`, which those callers have no `this` to invoke.
//   - `navbarTabMenuAction`/`saveNavbarTabOrder` are `this`-bound `function`
//     exports (invoked via `el.call(...)`), so their sibling calls route
//     through `this.call('navbarTabPrefs', root)`. `navbarTabMenuItems` is
//     ALSO `this`-bound, but its existing unit test
//     (navbarTabMenu.test.js) calls it with a hand-rolled minimal
//     `{ call: (name) => … }` mock that only stubs the route classifiers —
//     routing its `navbarTabPrefs` read through that same narrower mock
//     would silently return `undefined`, so it keeps a self-contained
//     inline copy instead (behavior-identical, just not sharing the
//     `this.call` indirection this file uses everywhere else it's safe to).
//   - The per-workspace save-queue (`_saveQueue`) is genuinely shared
//     mutable state across THREE exported entrypoints, one of which
//     (`toggleCustomNavbarPin`) has no `this`/`el` at all — it's re-homed
//     onto `root.__navbarSaveQueue` (every consumer receives `root`, unlike
//     `el`), the same escape hatch AppNavbar's `el.context.__appNavbarMore`
//     uses for its own cross-component shared state.

// Pure read — the active workspace's navbar-tab prefs, always shape-complete.
// A null workspace key (not resolved yet) simply finds no bag — same as any
// other unrecognized key — so this needs no special-casing.
export const navbarTabPrefs = (root) => {
  const wsKey = (r) => {
    const id = r?.workspace?.id || r?.workspace?._id || r?.jwtClaims?.active_workspace_id
    return id ? String(id) : null
  }
  const empty = { hidden: [], pinned: [], order: [], pinnedCustom: [] }
  const bag = (root?.userPrefs?.navbar_tabs || {})[wsKey(root)]
  return {
    hidden: Array.isArray(bag?.hidden) ? bag.hidden : empty.hidden,
    pinned: Array.isArray(bag?.pinned) ? bag.pinned : empty.pinned,
    // Explicit drag arrangement (long-press → reorder, 2026-07-30 per Nika).
    // A full path list; when present it is the arrangement AUTHORITY.
    order: Array.isArray(bag?.order) ? bag.order : empty.order,
    // Custom (foreign) pins — see the header comment above.
    pinnedCustom: Array.isArray(bag?.pinnedCustom) ? bag.pinnedCustom : empty.pinnedCustom
  }
}

// Bounded wait for the workspace persistence key to resolve — covers the
// (normally sub-frame, synchronously-seeded) boot window and the brief gap
// mid workspace-switch where root.workspace/jwtClaims can both still be
// unset. 15 × 200ms = 3s, well inside the 5s budget reconcileMwtOrgFromUrl
// already uses for the analogous "org not resolved yet" wait
// (mwtRouter.js) — same shape, same order of magnitude, so a page that's
// merely slow to hydrate behaves the same everywhere. Returns null (never
// 'default' — see the header comment on why a fallback key is refused
// outright) if it never resolves. Exported (underscore-prefixed, like
// bootShell.js's _causeChain/_isTokenRejection) so its retry/timeout
// behavior is directly unit-testable with a fast attempts/delayMs override
// instead of a multi-second real wait. Deliberately NOT in the functions
// barrel (functions/index.js) — tried 2026-08-09, reverted, a documented
// dropped-export finding for this exact underscore-export shape — so it's
// consumed via plain ESM import only (tests), never `el.call`.
// @testOnly — exported for __tests__/navbarTabMenuSave.test.js; no live code imports or calls it.
export const _resolveWsKey = async (root, { attempts = 15, delayMs = 200 } = {}) => {
  const wsKey = (r) => {
    const id = r?.workspace?.id || r?.workspace?._id || r?.jwtClaims?.active_workspace_id
    return id ? String(id) : null
  }
  for (let i = 0; i < attempts; i++) {
    const key = wsKey(root)
    if (key) return key
    await new Promise((r) => setTimeout(r, delayMs))
  }
  return null
}

// Pure transform for HeaderTabs' reactive children: drop the user's removed
// tabs, then arrange. An explicit drag ORDER (set via long-press reorder)
// wins outright — it IS the user's arrangement, pins included; paths the
// order doesn't know (a module tab installed after the drag) append in their
// natural spot. Without one, the pinned tabs stable-sort to the front (pin
// order = the order they pinned in) and the rest keep the workspace/default
// arrangement — exactly the pre-order behavior.
//
// Custom pins merge in BEFORE the hidden/order/pinned math runs, so all
// three apply to them exactly like a base tab (a custom pin can be dragged,
// hidden or reset the same as any other row) — the ONE place every consumer
// (HeaderTabs' desktop Tabs + its MobileTabs strip) renders the arrangement,
// so a search-result pin appears in both without either needing its own
// merge logic.
// `opts.labelKeyFor(path)` (optional, from HeaderTabs: the workspace's
// launcherTitleKeyFor) gives a bare built-in route its label KEY, so a LEGACY
// custom pin that stored resolved text renders in the active language.
export const applyNavbarTabPrefs = (root, list, opts) => {
  // Inlined navbarTabPrefs (self-contained — a plain arrow with no `this`,
  // so a bare sibling-export call is exactly as unresolvable post-frank as
  // a module-local read; see the file-header FA204/FA207 comment).
  const wsKey = (r) => {
    const id = r?.workspace?.id || r?.workspace?._id || r?.jwtClaims?.active_workspace_id
    return id ? String(id) : null
  }
  const empty = { hidden: [], pinned: [], order: [], pinnedCustom: [] }
  const bag = (root?.userPrefs?.navbar_tabs || {})[wsKey(root)]
  const prefs = {
    hidden: Array.isArray(bag?.hidden) ? bag.hidden : empty.hidden,
    pinned: Array.isArray(bag?.pinned) ? bag.pinned : empty.pinned,
    order: Array.isArray(bag?.order) ? bag.order : empty.order,
    pinnedCustom: Array.isArray(bag?.pinnedCustom) ? bag.pinnedCustom : empty.pinnedCustom
  }
  const base = Array.isArray(list) ? list : []
  const basePaths = new Set(base.map((t) => t && t.path))
  // A custom pin's stored `label` is a one-shot copy of the search-result /
  // drag-ref title AT PIN TIME — English when the shell ran in English then,
  // and stale the day the module renames the page (measured 2026-09-23: the
  // aihouse Feedback page, pinned as "Feedback", stayed English on a Georgian
  // workspace while its base tabs translated). The name the workspace
  // DECLARES for that route is live: the resolved navbar/launcher, then the
  // module's own configDefaults, then each additive app (the same order
  // pageContext.js's moduleRouteTitle reads). A declared label is a bundle
  // key or a literal, and the tab's own `polyglot` call resolves either, so
  // it replaces the copy at render time; the copy stays the fallback for a
  // route nothing declares. A pin that carries a query or hash is a RECORD
  // pin (one person, one document) whose stored name is the record's own,
  // so the declared name of its bare route must never rename it. Inlined,
  // like `wsKey`: this file's exports cannot reach one another after frank
  // (see the header).
  const declaredLabel = (r, path) => {
    const p = String(path || '')
    if (!p || p !== p.split('#')[0].split('?')[0]) return ''
    const lists = []
    const ws = r?.workspaceSettings
    if (ws && typeof ws === 'object') lists.push(ws.navbar, ws.apps)
    const cd = r?.workspaceProject?.configDefaults
    if (cd && typeof cd === 'object') lists.push(cd.navbar, cd.apps)
    if (Array.isArray(r?.workspaceApps)) {
      for (const app of r.workspaceApps) {
        if (app && typeof app === 'object') lists.push(app.navbar, app.apps)
      }
    }
    for (const l of lists) {
      if (!Array.isArray(l)) continue
      for (const item of l) {
        if (!item || typeof item !== 'object') continue
        const route = String(item.path || item.route || item.href || '')
        if (route.split('#')[0].split('?')[0] !== p) continue
        const label = String(item.label || item.title || item.name || '')
        if (label) return label
      }
    }
    return ''
  }
  const custom = prefs.pinnedCustom
    .filter((c) => c && c.path && !basePaths.has(c.path))
    .map((c) => ({
      key: `custom:${c.path}`,
      // A stored KEY first (pins made since keys were stored), then the name
      // the workspace declares, then the built-in route's launcher key (a
      // legacy text pin — never for a RECORD pin with a query or hash), and
      // the stored text last, for a foreign path nothing declares.
      label:
        c.labelKey ||
        declaredLabel(root, c.path) ||
        (opts &&
        typeof opts.labelKeyFor === 'function' &&
        String(c.path) === String(c.path).split('#')[0].split('?')[0]
          ? opts.labelKeyFor(c.path) || ''
          : '') ||
        c.label ||
        c.path,
      path: c.path,
      custom: true
    }))
  if (!base.length && !custom.length) return list
  const combined = custom.length ? [...base, ...custom] : base
  const hidden = new Set(prefs.hidden)
  const kept = hidden.size ? combined.filter((t) => t && !hidden.has(t.path)) : combined.slice()
  if (prefs.order.length) {
    const rank = new Map(prefs.order.map((path, i) => [path, i]))
    return kept.sort(
      (a, b) =>
        (rank.has(a.path) ? rank.get(a.path) : Infinity) -
        (rank.has(b.path) ? rank.get(b.path) : Infinity)
    )
  }
  if (!prefs.pinned.length && !custom.length) return kept
  const rank = new Map(prefs.pinned.map((path, i) => [path, i]))
  return kept.sort(
    (a, b) =>
      (rank.has(a.path) ? rank.get(a.path) : Infinity) -
      (rank.has(b.path) ? rank.get(b.path) : Infinity)
  )
}

// Per-workspace-key write queue. `workspaceProject.userPreferences · update`
// REPLACES the whole row server-side (no merge — see the header comment), so
// two edits fired close together (pin, then immediately hide a different
// tab) race their OWN network round-trips: the client-side optimistic state
// is always correct (each action reads navbarTabPrefs(root) fresh, which
// already reflects the prior action's synchronous root.update), but without
// this queue the SERVER saw "whichever PUT happened to arrive last", not
// "whichever was requested last" — a slower first write could land AFTER a
// faster second one and silently overwrite it, so the second edit vanished
// on the next reload even though it was visibly applied the whole session.
// Chaining every write for the SAME key onto the tail of the previous one
// forces server arrival order to match request order. Keyed by workspace so
// two different workspaces' saves (e.g. two browser tabs) never wait on
// each other.
//
// FA204 / frank trap — this queue is genuinely shared mutable state across
// THREE exported entrypoints (saveNavbarTabOrder, navbarTabMenuAction,
// toggleCustomNavbarPin), and one of them (toggleCustomNavbarPin) is a
// plain arrow with no `this`/`el` at all — every consumer DOES receive
// `root`, though, so the queue lives on `root.__navbarSaveQueue` (lazily
// created) instead of a module-scope Map. Each of the three callers below
// carries its own self-contained copy of this save routine (duplicated —
// object/module-scope helpers can't be shared across different exported
// functions any more than across different components), all reading/
// writing the SAME `root.__navbarSaveQueue`, so the ordering guarantee
// holds regardless of which entrypoint fired the write.

// Persist a drag arrangement (HeaderTabs long-press reorder). `orderPaths` is
// the FULL visible path list in its new order — stored as the arrangement
// authority (see applyNavbarTabPrefs). el.call('saveNavbarTabOrder', paths)
export const saveNavbarTabOrder = async function (orderPaths) {
  const root = this?.state?.root || this?.state
  if (!root || !Array.isArray(orderPaths) || !orderPaths.length) return
  const prefs = this.call('navbarTabPrefs', root)
  await saveNavbarTabPrefsLocal(root, { ...prefs, order: orderPaths.filter(Boolean) })

  async function saveNavbarTabPrefsLocal(r, next) {
    const wsKey = (x) => {
      const id = x?.workspace?.id || x?.workspace?._id || x?.jwtClaims?.active_workspace_id
      return id ? String(id) : null
    }
    let resolvedKey = null
    for (let i = 0; i < 15; i++) {
      resolvedKey = wsKey(r)
      if (resolvedKey) break
      await new Promise((res) => setTimeout(res, 200))
    }
    if (!resolvedKey) {
      console.warn('[navbarTabMenu] prefs save refused — workspace not resolved yet')
      return
    }
    const prefsPatch = {
      ...(r.userPrefs || {}),
      navbar_tabs: { ...(r.userPrefs?.navbar_tabs || {}), [resolvedKey]: next }
    }
    r.update({ userPrefs: prefsPatch }, { preventFetch: true })
    if (!r.__navbarSaveQueue) r.__navbarSaveQueue = new Map()
    const queue = r.__navbarSaveQueue
    const prior = queue.get(resolvedKey) || Promise.resolve()
    const task = prior
      .catch(() => {})
      .then(() =>
        getSDK().execute('workspaceProject.userPreferences', 'update', { payload: prefsPatch })
      )
    queue.set(resolvedKey, task)
    try {
      await task
    } catch (e) {
      console.warn('[navbarTabMenu] prefs save failed', e)
    }
  }
}

// itemsFn — `el.call('navbarTabMenuItems', root)` (this = the Menu el).
// `root.contextDropdown.navTab` carries the right-clicked tab ({ key, label,
// path }) — null when the pill background was right-clicked, which shows only
// the reset row (the escape hatch once every tab has been removed).
export const navbarTabMenuItems = function (root) {
  const cm = root && root.contextDropdown
  if (!cm || !cm.open) return []
  // Inlined navbarTabPrefs (self-contained — the test-mocked `this.call`
  // for this export only stubs the route classifiers below, not the prefs
  // registry entry, so a `this.call('navbarTabPrefs', …)` indirection would
  // silently break that existing, narrower mock contract).
  const prefs = (() => {
    const wsKey = (r) => {
      const id = r?.workspace?.id || r?.workspace?._id || r?.jwtClaims?.active_workspace_id
      return id ? String(id) : null
    }
    const empty = { hidden: [], pinned: [], order: [], pinnedCustom: [] }
    const bag = (root?.userPrefs?.navbar_tabs || {})[wsKey(root)]
    return {
      hidden: Array.isArray(bag?.hidden) ? bag.hidden : empty.hidden,
      pinned: Array.isArray(bag?.pinned) ? bag.pinned : empty.pinned,
      order: Array.isArray(bag?.order) ? bag.order : empty.order,
      pinnedCustom: Array.isArray(bag?.pinnedCustom) ? bag.pinnedCustom : empty.pinnedCustom
    }
  })()
  const customized =
    prefs.hidden.length > 0 ||
    prefs.pinned.length > 0 ||
    prefs.order.length > 0 ||
    prefs.pinnedCustom.length > 0
  const t = cm.navTab
  const items = []
  if (t && t.path) {
    const pinned = prefs.pinned.includes(t.path)
    items.push({ key: 'pin', action: 'pin', label: pinned ? 'Unpin' : 'Pin' })
    // "Open in side view" only where a split can actually happen (2026-07-27
    // per Nika, both halves): the route must be SLOT-capable — canvas /
    // full-page routes (/studio, admin, data settings) always open as the
    // full page, openAppPage's isCanvasRoute/isFullPageRoute gate wins before
    // any slot logic, so the exact same registered classifiers decide here —
    // AND a PageSlot primary must already be open to split beside
    // (root._canvasSlot; without one openAppPage falls back to a plain open,
    // so the row would lie). Same-route split is a no-op re-focus — hidden
    // too. try/catch mirrors accountMenuItems: the classifiers are absent
    // outside the workspace shell, where these routes don't exist anyway.
    let fullPage = false
    try {
      fullPage = !!(this.call('isCanvasRoute', t.path) || this.call('isFullPageRoute', t.path))
    } catch (_) {
      fullPage = false
    }
    const slotOpen = !!root._canvasSlot && root._canvasSlot !== t.path
    if (!fullPage && slotOpen) {
      items.push({ key: 'sideview', action: 'sideview', label: 'shell.navbarTabMenu.sideView' })
    }
    items.push({
      key: 'remove',
      action: 'remove',
      label: 'shell.navbarTabMenu.remove',
      danger: true
    })
  }
  if (customized) {
    items.push({ key: 'reset', action: 'reset', label: 'shell.navbarTabMenu.reset' })
  }
  return items
}

// actionFn — `el.call('navbarTabMenuAction', action, item)` from
// ContextDropdownItem.onClick (this = the clicked row el). Owns the close.
export const navbarTabMenuAction = async function (action) {
  const root = this.state.root
  const t = root.contextDropdown?.navTab || null
  const prefs = this.call('navbarTabPrefs', root)
  root.update({ contextDropdown: null }, { preventFetch: true })

  if (action === 'sideview' && t?.path) {
    // Second-pane open (iPad-style split). openAppPage falls back to a plain
    // primary open when nothing is on screen to split against; el.router is
    // the last resort where openAppPage isn't registered (no-backend shells).
    try {
      this.call('openAppPage', t.path, { preferSlot: true, split: true })
    } catch (_) {
      this.router(t.path, this.getRoot())
    }
    return
  }

  if (action === 'pin' && t?.path) {
    const pinned = prefs.pinned.includes(t.path)
      ? prefs.pinned.filter((p) => p !== t.path)
      : [...prefs.pinned, t.path]
    await saveNavbarTabPrefsLocal(root, { ...prefs, pinned })
    return
  }

  if (action === 'remove' && t?.path) {
    await saveNavbarTabPrefsLocal(root, {
      // Spread the existing bag first — this write only means to touch
      // hidden/pinned/order below; a bare `{ hidden, pinned, order }`
      // literal here would silently drop `pinnedCustom` (update REPLACES
      // the per-workspace bag, it doesn't deep-merge — see
      // saveNavbarTabPrefsLocal above), erasing every search-pinned tab the
      // instant an unrelated tab was removed.
      ...prefs,
      hidden: prefs.hidden.includes(t.path) ? prefs.hidden : [...prefs.hidden, t.path],
      // A removed tab can't stay pinned — reset would otherwise resurrect it
      // at the front instead of its natural spot. Same for the drag order.
      pinned: prefs.pinned.filter((p) => p !== t.path),
      order: prefs.order.filter((p) => p !== t.path),
      // A removed CUSTOM tab (synthesized from a search-result pin) has no
      // "natural spot" to fall back to — it only ever existed because it was
      // pinned, so removing it must drop the pinnedCustom row outright too,
      // the same way `toggleCustomNavbarPin` unpins it.
      pinnedCustom: prefs.pinnedCustom.filter((c) => c.path !== t.path)
    })
    return
  }

  if (action === 'reset') {
    await saveNavbarTabPrefsLocal(root, { hidden: [], pinned: [], order: [], pinnedCustom: [] })
  }

  // Self-contained save routine — duplicated verbatim from
  // saveNavbarTabOrder's own copy; see the FA204 comment above _saveQueue's
  // (now removed) module-scope declaration for why it can't be shared.
  async function saveNavbarTabPrefsLocal(r, next) {
    const wsKey = (x) => {
      const id = x?.workspace?.id || x?.workspace?._id || x?.jwtClaims?.active_workspace_id
      return id ? String(id) : null
    }
    let resolvedKey = null
    for (let i = 0; i < 15; i++) {
      resolvedKey = wsKey(r)
      if (resolvedKey) break
      await new Promise((res) => setTimeout(res, 200))
    }
    if (!resolvedKey) {
      console.warn('[navbarTabMenu] prefs save refused — workspace not resolved yet')
      return
    }
    const prefsPatch = {
      ...(r.userPrefs || {}),
      navbar_tabs: { ...(r.userPrefs?.navbar_tabs || {}), [resolvedKey]: next }
    }
    r.update({ userPrefs: prefsPatch }, { preventFetch: true })
    if (!r.__navbarSaveQueue) r.__navbarSaveQueue = new Map()
    const queue = r.__navbarSaveQueue
    const prior = queue.get(resolvedKey) || Promise.resolve()
    const task = prior
      .catch(() => {})
      .then(() =>
        getSDK().execute('workspaceProject.userPreferences', 'update', { payload: prefsPatch })
      )
    queue.set(resolvedKey, task)
    try {
      await task
    } catch (e) {
      console.warn('[navbarTabMenu] prefs save failed', e)
    }
  }
}

// Toggle a CUSTOM navbar pin — a foreign `{ path, label }` (not part of
// HEADER_TABS / workspaceSettings.navbar) pinned in from elsewhere, e.g. a
// global-search result (workspace/functions/navSearchResultMenu.js). Plain
// export (no `el.call` needed) — same direct-import contract as
// navbarTabPrefs/applyNavbarTabPrefs above, consumed by the search-result
// menu's actionFn. el.call('navSearchResultMenuAction', 'pin') is the caller.
// A plain arrow with no `this`, so — like applyNavbarTabPrefs — its
// navbarTabPrefs read AND its save routine are both self-contained locals
// rather than `this.call`, which this function has no `this` to invoke.
export const toggleCustomNavbarPin = async (root, entry) => {
  if (!root || !entry || !entry.path) return
  const wsKeyRead = (r) => {
    const id = r?.workspace?.id || r?.workspace?._id || r?.jwtClaims?.active_workspace_id
    return id ? String(id) : null
  }
  const empty = { hidden: [], pinned: [], order: [], pinnedCustom: [] }
  const bag = (root?.userPrefs?.navbar_tabs || {})[wsKeyRead(root)]
  const prefs = {
    hidden: Array.isArray(bag?.hidden) ? bag.hidden : empty.hidden,
    pinned: Array.isArray(bag?.pinned) ? bag.pinned : empty.pinned,
    order: Array.isArray(bag?.order) ? bag.order : empty.order,
    pinnedCustom: Array.isArray(bag?.pinnedCustom) ? bag.pinnedCustom : empty.pinnedCustom
  }
  const exists = prefs.pinnedCustom.some((c) => c.path === entry.path)
  const pinnedCustom = exists
    ? prefs.pinnedCustom.filter((c) => c.path !== entry.path)
    : [
        ...prefs.pinnedCustom,
        (() => {
          // Store the row's KEY when it has one (an explicit `labelKey`, or a
          // `label` that IS a key, e.g. a launcher tile's title): the tab then
          // re-resolves on every language switch. The text stays as the
          // fallback name.
          const label = entry.label || entry.path
          const keyShaped = (v) =>
            typeof v === 'string' && /^[a-z][A-Za-z0-9_-]*(\.[A-Za-z0-9_-]+)+$/.test(v)
          const labelKey = entry.labelKey || (keyShaped(entry.label) ? entry.label : '')
          return labelKey ? { path: entry.path, label, labelKey } : { path: entry.path, label }
        })()
      ]

  const next = { ...prefs, pinnedCustom }
  let resolvedKey = null
  for (let i = 0; i < 15; i++) {
    resolvedKey = wsKeyRead(root)
    if (resolvedKey) break
    await new Promise((res) => setTimeout(res, 200))
  }
  if (!resolvedKey) {
    console.warn('[navbarTabMenu] prefs save refused — workspace not resolved yet')
    return
  }
  const prefsPatch = {
    ...(root.userPrefs || {}),
    navbar_tabs: { ...(root.userPrefs?.navbar_tabs || {}), [resolvedKey]: next }
  }
  root.update({ userPrefs: prefsPatch }, { preventFetch: true })
  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  const prior = queue.get(resolvedKey) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(() =>
      getSDK().execute('workspaceProject.userPreferences', 'update', { payload: prefsPatch })
    )
  queue.set(resolvedKey, task)
  try {
    await task
  } catch (e) {
    console.warn('[navbarTabMenu] prefs save failed', e)
  }
}
